// mapbox
import mapboxgl from 'mapbox-gl';

// config
import measureRecordLayers from '@/vue3/configs/measureRecordLayers';

// services
import { useService } from '@/vue3/api/services';

export const useMeasureRecord = () => {
    // services
    const { getGeoRecordArea, getGeoRecordLength, getGeoRecordPerimeter } =
        useService();

    // methods
    const addSurroundingVertex = (features, measureRecord, isMeters) => {
        const recordFeaturesLength = Object.keys(measureRecord.features).length;
        const firstRecord = measureRecord.features[0];
        const lastRecord = measureRecord.features[recordFeaturesLength - 1];

        const firstVertex = {
            geometry: {
                coordinates: firstRecord.geometry.coordinates[0],
                type: 'Point',
            },
            id: measureRecord.id,
            properties: {
                isVisible: true,
                measurementTotal: firstRecord.properties.measurementTotal,
                totalMeasurementLabel: `${
                    Math.round(
                        firstRecord.properties.measurementTotal *
                            100 *
                            (isMeters ? 1 : 1.09361),
                    ) / 100
                } ${isMeters ? 'm' : 'yd'}`,
                measureRecordId: measureRecord.id,
            },
            type: 'Feature',
        };

        const lastVertex = {
            geometry: {
                coordinates: lastRecord.geometry.coordinates[1],
                type: 'Point',
            },
            id: measureRecord.id,
            properties: {
                isVisible: true,
                measurementTotal: firstRecord.properties.measurementTotal,
                totalMeasurementLabel: `${
                    Math.round(
                        lastRecord.properties.measurementTotal *
                            100 *
                            (isMeters ? 1 : 1.09361),
                    ) / 100
                } ${isMeters ? 'm' : 'yd'}`,
                measureRecordId: measureRecord.id,
            },
            type: 'Feature',
        };

        features.push(firstVertex);

        for (const i in measureRecord.features) {
            measureRecord.features[i].properties.isVisible = true;
            measureRecord.features[i].properties.measureRecordId =
                measureRecord.id;
            measureRecord.features[i].id = measureRecord.id;

            if (!isMeters) {
                let measurement =
                    measureRecord.features[i].properties.measurement;
                measureRecord.features[i].properties.measurementLabel = `${
                    Math.round(measurement * 100 * 1.09361) / 100
                } yd`;
            }
            features.push(measureRecord.features[i]);
        }

        features.push(lastVertex);

        return features;
    };

    const addMeasureRecord = (
        map: mapboxgl.Map,
        measureRecord: any,
        isMeters: boolean,
    ) => {
        const source = map.getSource('measure-records');
        let features = source._data.features;

        measureRecord.features.forEach((feature) => {
            if (feature.properties.measurement === 0) return;

            if (feature.geometry.type === 'LineString') {
                features = addSurroundingVertex(
                    features,
                    measureRecord,
                    isMeters,
                );
            } else {
                feature.properties.isVisible = true;
                feature.id = measureRecord.id;
                feature.properties.measureRecordId = measureRecord.id;

                if (!isMeters) {
                    let measurement = feature.properties.measurement;
                    feature.properties.measurementLabel = `${
                        Math.round(measurement * 100 * 1.195990046301) / 100
                    } yd`;
                }
                features.push(feature);
            }

            source.setData({
                type: 'FeatureCollection',
                features: features,
            });
        });
    };

    const removeMeasureRecord = (map: mapboxgl.Map, id: number) => {
        const source = map.getSource('measure-records');
        let features = source._data.features;

        features = features.filter(
            (feature) => feature.properties.measureRecordId !== id,
        );

        source.setData({
            type: 'FeatureCollection',
            features: features,
        });
    };

    const setAllMeasureRecordVisibility = (
        map: mapboxgl.Map,
        isVisible: boolean,
    ) => {
        if (isVisible === undefined) {
            isVisible = true;
        }

        const source = map.getSource('measure-records');
        const features = source._data.features;
        features.forEach((feature) => {
            feature.properties.isVisible = isVisible;
        });
        source.setData({
            type: 'FeatureCollection',
            features: features,
        });
    };

    const initMeasureRecordSource = (
        map: mapboxgl.Map,
        measureRecords: any,
        isMeters: boolean,
        isVisible: boolean,
    ) => {
        const measureRecordSource = {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: <any>[],
            },
        };

        measureRecords.forEach((record) => {
            const type = record.geometry.type;

            if (type === 'Polygon') {
                measureRecordSource.data.features.push({
                    id: record.properties.measureRecordId,
                    type: 'Feature',
                    geometry: record.geometry,
                    properties: {
                        measureRecordId: record.properties.measureRecordId,
                        isVisible: isVisible,
                        measurement: record.properties.measurement,
                        measurementLabel: `${
                            Math.round(
                                record.properties.measurement *
                                    100 *
                                    (isMeters ? 1 : 1.195990046301),
                            ) / 100
                        } ${isMeters ? `m²` : `yd²`}`,
                        totalMeasurementLabel: null,
                    },
                });
            }

            if (type === 'LineString') {
                measureRecordSource.data.features.push({
                    id: record.properties.measureRecordId,
                    type: 'Feature',
                    geometry: record.geometry,
                    properties: {
                        measureRecordId: record.properties.measureRecordId,
                        isVisible: isVisible,
                        measurement: record.properties.measurement,
                        measurementLabel: `${
                            Math.round(
                                record.properties.measurement *
                                    100 *
                                    (isMeters ? 1 : 1.09361),
                            ) / 100
                        } ${isMeters ? `m` : `yd`}`,
                        totalMeasurementLabel: null,
                    },
                });
            }

            if (type === 'Point') {
                measureRecordSource.data.features.push({
                    id: record.properties.measureRecordId,
                    type: 'Feature',
                    geometry: record.geometry,
                    properties: {
                        measureRecordId: record.properties.measureRecordId,
                        isVisible: isVisible,
                        measurement: null,
                        measurementLabel: null,
                        totalMeasurementLabel: `${
                            Math.round(
                                record.properties.measurementTotal *
                                    100 *
                                    (isMeters ? 1 : 1.09361),
                            ) / 100
                        } ${isMeters ? 'm' : 'yd'}`,
                    },
                });
            }
        });

        map.addSource('measure-records', measureRecordSource);
    };

    const initMeasureRecordLayers = (map: mapboxgl.Map) => {
        measureRecordLayers.forEach((layer) => {
            map.addLayer(layer);
        });
    };

    const removeMeasureRecordLayers = (map: mapboxgl.Map) => {
        measureRecordLayers.forEach((layer) => {
            map.removeLayer(layer.id);
        });
    };

    const calculateTotalMeasurement = async (geoRecord: any, type: string) => {
        if (type === 'Polygon') {
            const area = getGeoRecordArea(geoRecord.id);
            const perimeter = getGeoRecordPerimeter(geoRecord.id);
            geoRecord.area = await area;
            geoRecord.perimeter = await perimeter;
        } else if (type === 'LineString') {
            geoRecord.length = await getGeoRecordLength(geoRecord.id);
        }
        return geoRecord;
    };

    const filterMeasureRecordAndAddSurroundingVertex = (
        measureRecords,
        isMeters,
    ) => {
        let measureFeatures: any = [];

        if (measureRecords === null) return measureFeatures;

        measureRecords.forEach((record) => {
            const recordFeaturesLength = Object.keys(record.features).length;

            if (recordFeaturesLength === 0) {
                return;
            }

            const featureKeys = Object.keys(record.features);

            if (record.features[featureKeys[0]].geometry.type === 'Polygon') {
                record.features[featureKeys[0]].properties.measureRecordId = record.id;
                measureFeatures.push(record.features[featureKeys[0]]);
                return;
            }

            measureFeatures.push(...addSurroundingVertex([], record, isMeters));
        });

        return measureFeatures;
    };

    return {
        addSurroundingVertex,
        addMeasureRecord,
        removeMeasureRecord,
        removeMeasureRecordLayers,
        initMeasureRecordSource,
        initMeasureRecordLayers,
        setAllMeasureRecordVisibility,
        calculateTotalMeasurement,
        filterMeasureRecordAndAddSurroundingVertex,
    };
};
